import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Eye, EyeOff, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { changePassword } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";

export function ChangePasswordPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isForced =
    auth.status === "authenticated" && !!auth.user.must_change_password;

  const rules = [
    { ok: next.length >= 8, label: "Au moins 8 caractères" },
    { ok: /[A-Z]/.test(next), label: "Une majuscule" },
    { ok: /[0-9]/.test(next), label: "Un chiffre" },
    { ok: next === confirm && next.length > 0, label: "Confirmation identique" },
    { ok: next !== current && next.length > 0, label: "Différent de l'ancien" },
  ];
  const allOk = rules.every((r) => r.ok);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allOk) return;
    setLoading(true);
    setError(null);
    try {
      await changePassword(current, next);
      await auth.refreshUser();
      setSuccess(true);
      setTimeout(() => {
        if (auth.status === "authenticated" && auth.user.merchant_id) {
          navigate(isForced ? "/merchant/overview" : "/merchant/settings", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }, 900);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erreur lors du changement de mot de passe";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Composant réutilisé pour les 2 layouts (forced vs inline)
  // ─────────────────────────────────────────────────────────
  const formCard = (
    <div className="card">
      <div className="card-body">
        {success ? (
          <div className="flex flex-col items-center py-4 text-center">
            <CheckCircle2 size={40} className="text-success-600 mb-2" />
            <div className="font-semibold text-[#181c32]">Mot de passe mis à jour</div>
            <div className="text-xs text-[#7e8299] mt-1">Redirection…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <PwField
              label="Mot de passe actuel"
              placeholder={isForced ? "Mot de passe temporaire reçu par email" : "Votre mot de passe actuel"}
              value={current}
              onChange={setCurrent}
              show={showCurrent}
              toggle={() => setShowCurrent((v) => !v)}
              autoFocus
            />
            <PwField
              label="Nouveau mot de passe"
              placeholder="Nouveau mot de passe"
              value={next}
              onChange={setNext}
              show={showNext}
              toggle={() => setShowNext((v) => !v)}
            />
            <PwField
              label="Confirmer le nouveau mot de passe"
              placeholder="Retapez le nouveau mot de passe"
              value={confirm}
              onChange={setConfirm}
              show={showNext}
              toggle={() => setShowNext((v) => !v)}
            />

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-[#7e8299] pt-1">
              {rules.map((r) => (
                <li
                  key={r.label}
                  className={`flex items-center gap-1.5 ${r.ok ? "text-success-700" : ""}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      r.ok ? "bg-success-500" : "bg-slate-300"
                    }`}
                  />
                  {r.label}
                </li>
              ))}
            </ul>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={!allOk || loading}
              className="btn btn-primary w-full"
            >
              {loading ? "Mise à jour…" : "Mettre à jour mon mot de passe"}
            </button>

            {!isForced && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn btn-light w-full"
              >
                Annuler
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  // Layout 1 : FORCÉ (premier login après invite)
  // Fullscreen centré, pas de sidebar — le user doit d'abord changer avant d'accéder
  // ─────────────────────────────────────────────────────────
  if (isForced) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-200 px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-600 text-white mb-3">
              <KeyRound size={26} />
            </div>
            <h1 className="text-2xl font-bold text-[#181c32]">Choisissez votre mot de passe</h1>
            <p className="text-sm text-[#7e8299] mt-1">
              Un mot de passe temporaire vous a été communiqué par email. Pour votre sécurité, vous devez en définir un nouveau avant d'accéder au dashboard.
            </p>
          </div>
          {formCard}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Layout 2 : ROUTINE (depuis Paramètres ou dropdown profil)
  // Wrappé dans AppShell (sidebar + header), style page dashboard standard
  // ─────────────────────────────────────────────────────────
  return (
    <AppShell>
      <PageHeader
        title="Changer le mot de passe"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Paramètres", to: "/merchant/settings" }, { label: "Mot de passe" }]}
        actions={
          <button type="button" onClick={() => navigate("/merchant/settings")} className="btn btn-light btn-sm">
            <ArrowLeft size={14} />
            Retour aux paramètres
          </button>
        }
      />
      <div className="max-w-2xl">{formCard}</div>
    </AppShell>
  );
}

function PwField({
  label,
  placeholder,
  value,
  onChange,
  show,
  toggle,
  autoFocus,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  toggle: () => void;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#7e8299] font-bold">{label}</label>
      <div className="relative mt-1">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="form-input pr-10"
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7e8299] hover:text-[#181c32]"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
